import React, { Suspense, lazy } from 'react';
import './App.css';
import '@/services/confirmService'; // registers window.btConfirm
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CartProvider } from '@/context/CartContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CartDrawer from '@/components/CartDrawer';
import UrgencyBanner from '@/components/UrgencyBanner';
import StoreSelector from '@/components/StoreSelector';
import LidiaChatWidget from '@/components/LidiaChatWidget';
import FeedbackWidget from '@/components/FeedbackWidget';
import HomePage from '@/pages/HomePage';
import CatalogPage from '@/pages/CatalogPage';
import ProductDetailPage from '@/pages/ProductDetailPage';
import AboutPage from '@/pages/AboutPage';
// Heavy admin / staff panels are lazy-loaded so the storefront is snappy.
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const StorePanelPage = lazy(() => import('@/pages/StorePanelPage'));
const EditorPage = lazy(() => import('@/pages/EditorPage'));
const TrackOrderPage = lazy(() => import('@/pages/TrackOrderPage'));
const OrderQrPage = lazy(() => import('@/pages/OrderQrPage'));
const TeamLandingPage = lazy(() => import('@/pages/TeamLandingPage'));
const DespachoPage = lazy(() => import('@/pages/DespachoPage'));
const WishlistPage = lazy(() => import('@/pages/WishlistPage'));
const CustomerAccountPage = lazy(() => import('@/pages/CustomerAccountPage'));
import { MessageCircle } from 'lucide-react';
import { useSelectedStore } from '@/store/dataStore';
import { BIGTIME_WHATSAPP } from '@/mock/products';
import MobileBottomNav from '@/components/MobileBottomNav';

function FloatingWhatsApp() {
  const { store } = useSelectedStore();
  const target = store?.whatsapp || BIGTIME_WHATSAPP;
  return (
    <a href={`https://api.whatsapp.com/send?phone=${target}&text=${encodeURIComponent('Hola BIGTIME, me interesa...')}`} target="_blank" rel="noreferrer" aria-label="WhatsApp"
      data-testid="whatsapp-fab" className="fixed bottom-[var(--fab-base)] right-3 sm:right-5 z-30 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#25D366] hover:bg-[#1FBA57] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-110">
      <MessageCircle className="w-5 h-5 sm:w-[26px] sm:h-[26px]" />
    </a>
  );
}

function CustomerLayout({ children }) {
  const location = useLocation();
  const isBackoffice = ['/admin', '/tienda', '/editor', '/despacho', '/equipo', '/orden/'].some((p) => location.pathname.startsWith(p));
  // iter34 · Todos los paneles internos usan su propio InternalShell y NO
  // deben mostrar el header público (búsqueda, carrito, categorías Relojería…)
  // porque son solo para clientes. La ruta pública `/perfil` sigue teniéndolo.
  const isStandalone = isBackoffice;
  const skipStoreSelector = isBackoffice || location.pathname.startsWith('/mi-cuenta') || location.pathname.startsWith('/perfil') || location.pathname.startsWith('/pedido/');
  return (
    <>
      {!isBackoffice && <UrgencyBanner />}
      <CartProvider>
        {!isStandalone && <Header />}
        {!skipStoreSelector && <StoreSelector />}
        <main>{children}</main>
        {!isStandalone && <Footer />}
        {!isStandalone && <div className="mobile-bottom-spacer md:hidden" aria-hidden />}
        {!isStandalone && <MobileBottomNav />}
        <CartDrawer />
        {!isBackoffice && <FloatingWhatsApp />}
        {!isBackoffice && <LidiaChatWidget />}
        {!isBackoffice && <FeedbackWidget />}
      </CartProvider>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-slate-50">
      <BrowserRouter>
        <CustomerLayout>
          <Suspense fallback={<div className="p-10 text-center text-slate-400 text-sm">Cargando…</div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/catalogo" element={<CatalogPage />} />
            <Route path="/producto/:id" element={<ProductDetailPage />} />
            <Route path="/favoritos" element={<WishlistPage />} />
            <Route path="/nosotros" element={<AboutPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/tienda" element={<StorePanelPage />} />
            <Route path="/editor" element={<EditorPage />} />
            <Route path="/despacho" element={<DespachoPage />} />
            <Route path="/mi-cuenta" element={<CustomerAccountPage />} />
            <Route path="/perfil" element={<CustomerAccountPage />} />
            <Route path="/equipo" element={<TeamLandingPage />} />
            <Route path="/pedido/:code" element={<TrackOrderPage />} />
            <Route path="/orden/:code" element={<OrderQrPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </CustomerLayout>
      </BrowserRouter>
    </div>
  );
}

export default App;
